import * as React from 'react';
import { cn } from '@lib/utils';
import { useMemo } from 'react';

type Props = React.ComponentProps<'input'> & {
  error?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  textSize?: 'sm' | 'xs' | 'base';
  inputSize?: 'sm' | 'xs' | 'lg' | 'base';
  textAlign?: 'left' | 'right' | 'center';
  variant?: 'default' | 'secondary';
  hideNumberSpin?: boolean;
};

function Input({
  className,
  type,
  error,
  inputSize,
  startIcon,
  endIcon,
  textSize = 'sm',
  textAlign = 'left',
  hideNumberSpin = true,
  variant: variantProps,
  ...props
}: Props) {
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  const isPasswordField = type === 'password';
  const resolvedType = isPasswordField
    ? isPasswordVisible
      ? 'text'
      : 'password'
    : type;

  const textSizeMapping = useMemo(() => {
    switch (textSize) {
      case 'sm':
        return 'text-sm';
      case 'xs':
        return 'text-xs';
      case 'base':
        return 'text-base';
      default:
        return '';
    }
  }, [textSize]);

  const textAlignMapping = useMemo(() => {
    switch (textAlign) {
      case 'left':
        return 'text-left';
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
      default:
        return 'text-left';
    }
  }, [textAlign]);

  const size = useMemo(() => {
    switch (inputSize) {
      case 'xs':
        return 'h-6.5';
      case 'sm':
        return 'h-7.5';
      case 'lg':
        return 'h-11';
      default:
        return 'h-9.5';
    }
  }, [inputSize]);

  const variant = useMemo(() => {
    switch (variantProps) {
      case 'secondary':
        return 'rounded-[3px]';
      default:
        return 'rounded-[10px]';
    }
  }, [variantProps]);

  const borderColor = useMemo(() => {
    if (props.disabled) {
      return 'border-muted-foreground cursor-not-allowed opacity-50';
    }
    if (error) {
      return 'border-red-500';
    }
    if (variantProps === 'secondary') {
      return 'border-[#C6C6C6]';
    }

    return 'border-[#979797]';
  }, [props.disabled, error, variantProps]);

  return (
    <div
      className={cn(
        size,
        'relative flex items-center w-full font-medium border bg-transparent shadow-xs transition-[color,box-shadow] outline-none',
        borderColor,
        variant,
        className
      )}
    >
      {startIcon && (
        <span className="absolute left-3 text-muted-foreground">
          {startIcon}
        </span>
      )}
      <input
        className={cn(
          textSizeMapping,
          textAlignMapping,
          'h-full no-autofill-bg w-full min-w-0 bg-transparent px-3 py-1 outline-none placeholder:text-muted-foreground file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          startIcon && 'pl-10',
          isPasswordField ? 'pr-14' : endIcon && 'pr-10',
          type === 'number' && hideNumberSpin && 'hide-number-spin'
        )}
        data-slot="input"
        type={resolvedType}
        {...props}
      />
      {isPasswordField && (
        <button
          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          className="absolute right-3 text-xs text-muted-foreground hover:text-foreground"
          type="button"
          onClick={() => setIsPasswordVisible((prev) => !prev)}
        >
          {isPasswordVisible ? 'hide' : 'show'}
        </button>
      )}
      {!isPasswordField && endIcon && (
        <span className="absolute right-3 text-muted-foreground">
          {endIcon}
        </span>
      )}
    </div>
  );
}

export { Input };
